import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useThreadStore } from '../stores/threadStore'
import { useSearchStore } from '../stores/searchStore'
import ThreadCard from '../components/ThreadCard'
import SearchBar from '../components/SearchBar'
import SearchResults from '../components/SearchResults'
import FilterChips from '../components/FilterChips'
import TagPills from '../components/TagPills'

// Persist scroll position across navigations
let savedScrollTop = 0

export default function Research() {
  const { threads, loading, error, fetchThreads, createThread } = useThreadStore()
  const { query } = useSearchStore()
  const navigate = useNavigate()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    // Skip re-fetch if threads are already loaded — prevents stale title flash
    // on navigate-back (optimistic update gets overwritten by in-flight fetch)
    if (useThreadStore.getState().threads.length === 0) fetchThreads()
  }, [fetchThreads])

  // Restore scroll position when returning to this page
  useEffect(() => {
    const el = scrollContainerRef.current
    if (el && savedScrollTop > 0) {
      el.scrollTop = savedScrollTop
    }
  }, [threads])

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      savedScrollTop = scrollContainerRef.current.scrollTop
    }
  }

  const handleNewThread = async () => {
    if (creating) return
    setCreating(true)
    try {
      const thread = await createThread({})
      navigate(`/research/${thread.id}`)
    } finally {
      setCreating(false)
    }
  }

  const isSearching = query.trim().length > 0

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 pt-4 pb-2 shrink-0 flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <SearchBar />
        </div>
        <button
          onClick={() => navigate('/trash')}
          aria-label="Trash"
          className="p-2 text-text-muted hover:text-text-secondary transition-colors shrink-0"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
          </svg>
        </button>
      </div>

      <div ref={scrollContainerRef} onScroll={handleScroll} className="flex-1 min-h-0 overflow-y-auto px-4 pb-4 space-y-3">
        {isSearching && (
          <>
            <FilterChips />
            <TagPills />
            <SearchResults />
          </>
        )}
        {!isSearching && (
          <>
            {loading && threads.length === 0 ? (
              <div className="space-y-2">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-elevated rounded-2xl p-4 animate-pulse">
                    <div className="h-4 bg-text-muted/15 rounded-md w-2/3 mb-2.5" />
                    <div className="h-3 bg-text-muted/10 rounded-md w-full mb-2" />
                    <div className="flex gap-3">
                      <div className="h-3 bg-text-muted/10 rounded-md w-1/4" />
                      <div className="h-3 bg-text-muted/10 rounded-md w-1/5" />
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <p className="text-error text-sm mb-4">{error}</p>
                <button
                  onClick={() => fetchThreads()}
                  className="px-4 py-2 rounded-lg bg-elevated text-text-secondary text-sm font-medium hover:bg-bg transition-colors"
                >
                  Try again
                </button>
              </div>
            ) : threads.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <svg className="w-16 h-16 text-text-muted opacity-30 mb-4" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <h2 className="text-xl font-semibold mb-2">Welcome to Scrivaro</h2>
                <p className="text-text-secondary mb-6">Start your first Bible study thread.</p>
                <button
                  onClick={handleNewThread}
                  disabled={creating}
                  className="bg-accent text-bg px-6 py-3 rounded-lg font-medium hover:bg-accent-hover transition-colors disabled:opacity-50"
                >
                  New Thread
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {threads.map((thread) => (
                  <ThreadCard key={thread.id} thread={thread} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* FAB - now positioned relative to the flex layout */}
      {!isSearching && threads.length > 0 && (
        <button
          onClick={handleNewThread}
          disabled={creating}
          aria-label="New thread"
          className="absolute bottom-20 right-4 w-14 h-14 bg-accent text-bg rounded-full shadow-lg flex items-center justify-center hover:bg-accent-hover transition-colors disabled:opacity-50 z-10"
        >
          <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
        </button>
      )}
    </div>
  )
}
